import * as R from 'ramda';
import { put, call, select, takeLatest } from 'redux-saga/effects';
// components
import { openLoader, closeLoader } from '../../components/loader/actions';
import {
  checkReportFunction,
  transformSearchCriteriaBeforeReportPost,
} from '../../components/edit-report/helpers';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// report-common
import { generateDefaultReport } from '../../report-common';
// sagas
import { visitPageSaga } from '../../sagas';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// feature fleet-documents
import * as A from './actions';
import { mapSummaryItems } from './settings';
import {
  makeSelectReportType,
  makeSelectPagination,
  makeSelectUsedReport,
  makeSelectPageVisited,
  makeSelectTitleSortValues,
  makeSelectAvailableReports,
  makeSelectTableTitleFilters,
} from './selectors';
//////////////////////////////////////////////////

const endpoints = {
  [GC.FLEET_TRUCK_DOCUMENT_VIEW_REPORT]: {
    list: endpointsMap.truckDocumentViewList,
    export: endpointsMap.truckDocumentViewListExport,
    summary: endpointsMap.truckDocumentViewListSummary,
  },
  [GC.FLEET_DRIVER_DOCUMENT_VIEW_REPORT]: {
    list: endpointsMap.driverDocumentViewList,
    export: endpointsMap.driverDocumentViewListExport,
    summary: endpointsMap.driverDocumentViewListSummary,
  },
  [GC.FLEET_TRAILER_DOCUMENT_VIEW_REPORT]: {
    list: endpointsMap.trailerDocumentViewList,
    export: endpointsMap.trailerDocumentViewListExport,
    summary: endpointsMap.trailerDocumentViewListSummary,
  },
};

const getSystemFields = (reportType) => {
  const map = {
    [GC.FLEET_TRUCK_DOCUMENT_VIEW_REPORT]: GC.FIELD_TRUCK_GUID,
    [GC.FLEET_DRIVER_DOCUMENT_VIEW_REPORT]: GC.FIELD_DRIVER_GUID,
    [GC.FLEET_TRAILER_DOCUMENT_VIEW_REPORT]: GC.FIELD_TRAILER_GUID,
  };

  return [{ sequence: 100, name: R.prop(reportType, map) }];
};

export function* getItemListRequest(action) {
  try {
    yield put(A.setListLoading(true));

    const reportType = yield select(makeSelectReportType());
    const reportParams = yield select(makeSelectUsedReport());
    const availableReports = yield select(makeSelectAvailableReports());

    if (R.and(
      G.isNilOrEmpty(availableReports),
      G.isNilOrEmpty(G.getPropFromObject('fields', reportParams)),
    )) return;

    const pagination = yield select(makeSelectPagination());
    const titleOrderFields = yield select(makeSelectTitleSortValues());
    const titleFilterParams = yield select(makeSelectTableTitleFilters());

    const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();

    const orderFields = G.ifElse(
      G.isNotEmpty(titleOrderFields),
      R.values(titleOrderFields),
      G.getOrElse(reportParams, 'orderFields', []),
    );

    const searchCriteria = G.getSearchCriteriaWithTitleFilters(reportParams, titleFilterParams);

    const guids = R.pathOr(null, ['payload', 'guids'], action);

    const { limit, offset } = pagination;

    const reqBody = {
      guids,
      limit,
      offset,
      orderFields,
      systemFields: getSystemFields(reportType),
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      fields: G.getOrElse(reportParams, 'fields', []),
      searchCriteria: transformSearchCriteriaBeforeReportPost(searchCriteria),
    };

    const endpoint = R.path([reportType, 'list'], endpoints);

    const res = yield call(sendRequest, 'post', endpoint, { data: reqBody });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getItemListSuccess({ data, guids }));
    } else {
      yield call(G.handleFailResponse, res, 'getItemListRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'getItemListRequest exception');
  } finally {
    yield put(closeLoader());
    yield put(A.setListLoading(false));
  }
}

export function* getSummaryRequest() {
  try {
    const reportType = yield select(makeSelectReportType());
    const endpoint = R.path([reportType, 'summary'], endpoints);

    if (G.isNilOrEmpty(endpoint)) return;

    const reportParams = yield select(makeSelectUsedReport());
    const titleFilterParams = yield select(makeSelectTableTitleFilters());

    const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();

    const searchCriteria = G.getSearchCriteriaWithTitleFilters(reportParams, titleFilterParams);

    const options = {
      data: {
        aggregationFields: [
          {
            name: GC.FIELD_GUID,
            aggregation: 'COUNT',
          },
        ],
        groupByFields: [GC.FIELD_EXPIRATION_STATUS],
        [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
        searchCriteria: transformSearchCriteriaBeforeReportPost(searchCriteria),
      },
    };

    const res = yield call(sendRequest, 'post', endpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getSummarySuccess(mapSummaryItems(data)));
    } else {
      yield call(G.handleException, res, 'getSummaryRequest fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'getSummaryRequest exception');
  }
}

export function* handleAvailableReportsRequest({ notSetUsedReport }) {
  try {
    const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();

    if (G.isNilOrEmpty(currentBranchGuid)) return false;

    const reportType = yield select(makeSelectReportType());

    const params = {
      reportType,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
    };

    const res = yield call(sendRequest, 'get', endpointsMap.listReports, { params });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const reports = G.getReportsSortedBySeqFreez(data);
      const checkedReports = checkReportFunction(reports);

      yield put(A.setReports(checkedReports));

      if (R.not(R.prop('length', reports))) {
        if (R.not(notSetUsedReport)) {
          yield put(A.setUsedReport(generateDefaultReport(reportType)));
        }

        return;
      }

      if (R.not(notSetUsedReport)) {
        const defaultReport = G.findDefaultReport(checkedReports);
        const usedReport = R.or(defaultReport, generateDefaultReport(reportType));

        yield put(A.setUsedReport(usedReport));
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleAvailableReportsRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleAvailableReportsRequest exception');
  }
}

function* handleCreateReportRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();

    const searchCriteria = transformSearchCriteriaBeforeReportPost(R.path(['searchCriteria'], payload));

    const options = {
      data: R.mergeRight(payload, { searchCriteria, [GC.FIELD_BRANCH_GUID]: currentBranchGuid }),
    };

    const res = yield call(sendRequest, 'post', endpointsMap.report, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setUsedReport(R.head(checkReportFunction(R.of(Array, data)))));

      yield call(handleAvailableReportsRequest, { notSetUsedReport: true });
      yield call(getItemListRequest, { payload: true });
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateReportRequestSaga fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleCreateReportRequestSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleUpdateReportRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const searchCriteria = transformSearchCriteriaBeforeReportPost(R.path(['searchCriteria'], payload));

    const options = {
      data: R.assoc('searchCriteria', searchCriteria, payload),
    };

    const res = yield call(sendRequest, 'put', endpointsMap.report, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setUsedReport(R.head(checkReportFunction(R.of(Array, data)))));

      yield call(handleAvailableReportsRequest, { notSetUsedReport: true });
      yield call(getItemListRequest, { payload: { openLoader: true } });
    } else {
      yield call(G.handleFailResponse, res, 'handleUpdateReportRequestSaga fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleUpdateReportRequestSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleChangeDefaultReportSaga({ payload }) {
  try {
    yield put(openLoader());

    const res = yield call(sendRequest, 'put', endpointsMap.changeDefaultReport, { data: payload });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(handleAvailableReportsRequest, {});
      yield call(getItemListRequest, { payload: { openLoader: true } });
    } else {
      yield call(G.handleFailResponse, res, 'handleChangeDefaultReportSaga fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleChangeDefaultReportSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

export function* handleExportReportDataSaga({ payload }) {
  try {
    yield put(openLoader());

    const reportType = yield select(makeSelectReportType());
    const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();

    const { name, fields, fileType, orderFields, searchCriteria } = payload;

    const reqBody = {
      fields,
      orderFields,
      [GC.FIELD_REPORT_NAME]: name,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      searchCriteria: transformSearchCriteriaBeforeReportPost(searchCriteria),
    };

    const options = {
      data: reqBody,
      resType: 'arraybuffer',
      params: { format: fileType },
    };

    const endpoint = R.path([reportType, 'export'], endpoints);

    const res = yield call(sendRequest, 'post', endpoint, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'info', 'messages:downloading-file');
    } else {
      yield call(G.handleFailResponse, res, 'handleExportReportDataSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleExportReportDataSaga exception');
  }
}

export function* visitFleetDocumentsPageSaga({ payload }) {
  while (true) { // eslint-disable-line
    const { reportType, checkVisitPage } = payload;

    yield put(openLoader());

    yield call(visitPageSaga, payload, checkVisitPage);

    yield put(A.setReportType(reportType));

    const pageVisited = yield select(makeSelectPageVisited());

    if (G.isTrue(pageVisited)) {
      yield put(A.setInitialStateOmitReport());
    } else {
      yield put(A.setInitialState(reportType));
    }

    yield put(A.setReportPending());

    yield put(A.getSummaryRequest());
    yield call(handleAvailableReportsRequest, { payload, notSetUsedReport: pageVisited });

    yield call(getItemListRequest);

    yield put(closeLoader());

    break;
  }
}

export function* fleetDocumentsWatcherSaga() {
  // visit page
  yield takeLatest(
    [
      GC.VISIT_FLEET_TRUCK_DOCUMENTS_PAGE,
      GC.VISIT_FLEET_DRIVER_DOCUMENTS_PAGE,
      GC.VISIT_FLEET_TRAILER_DOCUMENTS_PAGE,
    ],
    visitFleetDocumentsPageSaga,
  );
  // report
  yield takeLatest(A.getSummaryRequest, getSummaryRequest);
  yield takeLatest(A.getItemListRequest, getItemListRequest);
  yield takeLatest(A.createReportRequest, handleCreateReportRequestSaga);
  yield takeLatest(A.updateReportRequest, handleUpdateReportRequestSaga);
  yield takeLatest(A.exportReportDataRequest, handleExportReportDataSaga);
  yield takeLatest(A.changeDefaultReportRequest, handleChangeDefaultReportSaga);
}

export default fleetDocumentsWatcherSaga;
